import { useState } from "react";
import { changePassword } from "../../../api/auth";
import { useAuth } from "context/AuthContext";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "../../../components/MDBox";
import MDModal from "../../../components/MDModal";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";
import MDInput from "../../../components/MDInput";
import MDAlert from "../../../components/MDAlert";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

import PropTypes from "prop-types";

export default function ChangePasswordModal({ user }) {
  const { user: currentUser } = useAuth();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  
  const [open, setOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", color: "info" });

  // Vérifier si l'utilisateur connecté est admin
  const isAdmin = currentUser?.role === "Admin";

  const handleSubmit = async () => {
    // Validation
    if (!newPassword || newPassword.length < 6) {
      setAlert({
        show: true,
        message: "Le mot de passe doit contenir au moins 6 caractères",
        color: "error"
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setAlert({
        show: true,
        message: "Les mots de passe ne correspondent pas",
        color: "error"
      });
      return;
    }

    setIsSubmitting(true);
    setAlert({ show: false, message: "", color: "info" });

    try {
      const response = await changePassword(user.user_id, newPassword);
      if (response.status === 200) {
        setAlert({
          show: true,
          message: `Mot de passe mis à jour avec succès pour ${user.username}`,
          color: "success"
        });
        
        // Reset form after success
        setTimeout(() => {
          setNewPassword("");
          setConfirmPassword("");
          setOpen(false);
          setAlert({ show: false, message: "", color: "info" });
        }, 2000);
      }
    } catch (error) {
      console.error("Error changing password:", error);
      
      let errorMessage = "Erreur lors du changement de mot de passe";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setAlert({
        show: true,
        message: errorMessage,
        color: "error"
      });
    }
    
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    setNewPassword("");
    setConfirmPassword("");
    setAlert({ show: false, message: "", color: "info" });
    setOpen(false);
  };

  // Ne pas afficher le bouton si l'utilisateur n'est pas admin
  if (!isAdmin) {
    return null;
  }

  return (
    <>
      <MDButton
        variant="contained"
        size="small"
        onClick={() => setOpen(true)}
        color="warning"
        sx={{
          width: 170,
          padding: "6px 0 !important",
          height: 40,
        }}
      >
        Changer le mot de passe
      </MDButton>

      <MDModal open={open} onClose={handleCancel}>
        <MDBox mt={25} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={6}>
              <Card>
                <MDBox
                  mt={-5}
                  mb={2}
                  mx={2}
                  variant="gradient"
                  sx={{
                    background: !darkMode ? "linear-gradient(195deg, #ff9800, #ff5722)" : "white",
                    boxShadow: darkMode ? "0 1px 6px 1px white" : "0 1px 6px 1px #ff9800",
                  }}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <MDBox mt={2} mb={2}>
                    <MDTypography
                      variant="h4"
                      fontWeight="bold"
                      color={darkMode ? "inherit" : "white"}
                    >
                      Changer le mot de passe
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      color={darkMode ? "inherit" : "white"}
                    >
                      Utilisateur: {user.username} ({user.first_name} {user.last_name})
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox p={3}>
                  {alert.show && (
                    <MDBox mb={2}>
                      <MDAlert color={alert.color} dismissible>
                        {alert.message}
                      </MDAlert>
                    </MDBox>
                  )}

                  <MDBox mb={2}>
                    <MDInput
                      type="password"
                      label="Nouveau mot de passe"
                      fullWidth
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </MDBox>

                  <MDBox mb={3}>
                    <MDInput
                      type="password"
                      label="Confirmer le mot de passe"
                      fullWidth
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </MDBox>

                  <MDBox display="flex" justifyContent="space-between">
                    <MDButton
                      variant="gradient"
                      color="secondary"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Annuler
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="warning"
                      onClick={handleSubmit}
                      disabled={isSubmitting || !newPassword || !confirmPassword}
                    >
                      {isSubmitting ? "Changement..." : "Changer le mot de passe"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>
    </>
  );
}

ChangePasswordModal.propTypes = {
  user: PropTypes.object.isRequired,
};
